import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getCycleKey, getPondKey } from 'common/constants/queryKeys'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import useCycleApi from 'common/hooks/useCycleApi'
import { useGetCompletedPonds, useGetPonds} from 'modules/pond/api/queries'
import { useSpeciesName } from 'modules/pond/api/selector'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { normalizeSpeciesName } from 'utils/analytics'

import { TRANSFER_REQUEST_CONFIG } from '.'
import { useGetShrimpTransfer } from './queries'
import {
  CreateShrimpTransferVariables,
  DeleteShrimpTransferVariables,
  EditShrimpTransferVariables,
} from './types'

// Prefixes of queries whose numbers change when shrimp move between ponds.
const TRANSFER_AFFECTED_KEY_PREFIXES = [
  'pond-dashboard',
  'pond-dashboard-chart',
  'pond-dashboard-revenue-chart',
  'org-yield-sizes-current',
  'org-yield-sizes-estimate',
  'org-yield-size-ponds',
  'org-yield-harvest',
]
// Backend recalculates CycleYieldSummary for both ponds in the background
// (DOF-1344), so refetch once more after it has had time to finish.
const RECALCULATION_DELAY_MS = 3000

/**
 * Refetch pond dashboards (source + destination), cycle history and the org
 * yield sections right after a transfer is created / edited / deleted, so
 * the user doesn't have to refresh manually (DOF-1344).
 */
const useRefetchTransferAffectedQueries = () => {
  const queryClient = useQueryClient()

  return () => {
    const refetch = () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          TRANSFER_AFFECTED_KEY_PREFIXES.includes(String(query.queryKey[0])),
      })
      queryClient.invalidateQueries({ queryKey: getCycleKey.all() })
    }
    refetch()
    setTimeout(refetch, RECALCULATION_DELAY_MS)
  }
}

export const useMutateCreateShrimpTransfer = () => {
  const cycleApi = useCycleApi()
  const queryClient = useQueryClient()
  const { farmId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { refetch } = useGetShrimpTransfer()
  const { refetch: refetchCompletedPonds } = useGetCompletedPonds()
  const { refetch: refetchPonds } = useGetPonds()
  const { t } = useTranslation(['common'])
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()
  const refetchTransferAffected = useRefetchTransferAffectedQueries()

  return useMutation(
    ({ body: data }: CreateShrimpTransferVariables) =>
      cycleApi.request({ ...TRANSFER_REQUEST_CONFIG.create, data }),
    {
      onSuccess: (_data, { body }) => {
        enqueueSnackbar(t('common:create.success'), { variant: 'success' })
        refetch()
        refetchCompletedPonds()
        refetchPonds()
        refetchTransferAffected()
        if (farmId) {
          queryClient.invalidateQueries({ queryKey: getPondKey.names(farmId) })
          queryClient.invalidateQueries({
            queryKey: getCycleKey.byFarmByPond(farmId, body.destination_pond_id),
          })
        }
        // Backend moves shrimp between both ponds in this single call — fire
        // exactly once here, never per-pond.
        const normalizedSpecies = normalizeSpeciesName(speciesName)
        trackEvent(
          AnalyticsEvents.transferRecordCreated,
          normalizedSpecies ? { species: normalizedSpecies } : undefined
        )
      },
    }
  )
}

export const useMutateUpdateShrimpTransfer = () => {
  const cycleApi = useCycleApi()
  const queryClient = useQueryClient()
  const { farmId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { refetch } = useGetShrimpTransfer()
  const { refetch: refetchCompletedPonds } = useGetCompletedPonds()
  const { refetch: refetchPonds } = useGetPonds()
  const { t } = useTranslation(['common'])
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()
  const refetchTransferAffected = useRefetchTransferAffectedQueries()

  return useMutation(
    ({ partialTransferId, body }: EditShrimpTransferVariables) =>
      cycleApi.request({
        ...TRANSFER_REQUEST_CONFIG.edit,
        url: `${TRANSFER_REQUEST_CONFIG.edit.url}/${partialTransferId}`,
        data: body,
      }),
    {
      onSuccess: (_data, { body }) => {
        enqueueSnackbar(t('common:edit.success'), { variant: 'success' })
        refetch()
        refetchCompletedPonds()
        refetchPonds()
        refetchTransferAffected()
        if (farmId) {
          queryClient.invalidateQueries({ queryKey: getPondKey.names(farmId) })
          queryClient.invalidateQueries({
            queryKey: getCycleKey.byFarmByPond(farmId, body.destination_pond_id),
          })
        }
        const normalizedSpecies = normalizeSpeciesName(speciesName)
        trackEvent(
          AnalyticsEvents.transferRecordUpdated,
          normalizedSpecies ? { species: normalizedSpecies } : undefined
        )
      },
    }
  )
}

export const useMutateDeleteShrimpTransfer = () => {
  const cycleApi = useCycleApi()
  const { enqueueSnackbar } = useSnackbar()
  const { refetch } = useGetShrimpTransfer()
  const { refetch: refetchCompletedPonds } = useGetCompletedPonds()
  const { refetch: refetchPonds } = useGetPonds()
  const { t } = useTranslation(['common'])
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()
  const refetchTransferAffected = useRefetchTransferAffectedQueries()

  return useMutation(
    (partialTransferId: DeleteShrimpTransferVariables | string) =>
      cycleApi.request({
        ...TRANSFER_REQUEST_CONFIG.delete,
        url: `${TRANSFER_REQUEST_CONFIG.delete.url}/${partialTransferId}`,
      }),
    {
      onSuccess: () => {
        enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
        refetch()
        refetchCompletedPonds()
        refetchPonds()
        refetchTransferAffected()
        const normalizedSpecies = normalizeSpeciesName(speciesName)
        trackEvent(
          AnalyticsEvents.transferRecordDeleted,
          normalizedSpecies ? { species: normalizedSpecies } : undefined
        )
      },
    }
  )
}
